import type { ImportedSteamInventoryItem, WalletLedgerEntry } from '@redd/contracts';
import { formatPrice } from './market-data';

export const contactEmail = '894454665@redd888.com';
export const legalRoutes = {
  agreement: { slug: 'user', title: '用户服务协议' },
  saleRules: { slug: 'trade', title: '饰品交易服务规则' },
  privacy: { slug: 'privacy', title: '隐私政策' },
} as const;

export function cardBadge(name: string, wear = '', quality = '') {
  const wears: Array<[RegExp, string, string]> = [
    [/factory new|崭新出厂/i, '崭新出厂', '#9bd68e'],
    [/minimal wear|略有磨损/i, '略有磨损', '#91c9a3'],
    [/field[- ]tested|久经沙场/i, '久经沙场', '#e4c178'],
    [/well[- ]worn|破损不堪/i, '破损不堪', '#dfa17b'],
    [/battle[- ]scarred|战痕累累/i, '战痕累累', '#ed8b8b'],
  ];
  const finish = /[（(](金色|Gold|全息|Holo|闪亮|Foil|闪耀|Glitter|透镜|Lenticular)[）)]/i.exec(name)?.[1];
  const finishNames: Record<string, string> = { gold: '金色', holo: '全息', foil: '闪亮', glitter: '闪耀', lenticular: '透镜' };
  const match = wears.find(([pattern]) => pattern.test(wear || name));
  const qualities: Array<[RegExp, string]> = [[/违禁|contraband/i, '违禁'], [/隐秘|covert/i, '隐秘'], [/非凡|extraordinary/i, '非凡'], [/保密|classified/i, '保密'], [/受限|restricted/i, '受限'], [/军规|mil-spec/i, '军规级'], [/工业|industrial/i, '工业级'], [/消费|consumer/i, '消费级'], [/卓越|remarkable/i, '卓越'], [/高级|high grade/i, '高级'], [/普通|base grade/i, '普通']];
  const qualityLabel = qualities.find(([pattern]) => pattern.test(quality))?.[1];
  return { label: match?.[1] ?? (finish ? finishNames[finish.toLowerCase()] ?? finish : qualityLabel || '未定级'), color: match?.[2] ?? '#c4cbd0', special: [/★/.test(name) ? '★' : '', /StatTrak|暗金/i.test(name) ? 'StatTrak™' : ''].filter(Boolean).join(' ') };
}

// A countdown is display-only: elapsed time never grants permission to trade.
export function inventoryRestriction(item: Pick<ImportedSteamInventoryItem, 'inventoryVerified' | 'tradable' | 'tradableAfter' | 'tradeRestrictionReason'>, now = Date.now()) {
  if (item.inventoryVerified === false) return '库存待核实';
  if (item.tradable) return undefined;
  if (item.tradeRestrictionReason === 'steam_untradable') return '不可交易';
  if (!item.tradableAfter && item.tradeRestrictionReason !== 'steam_trade_cooldown') return '不可交易';
  const remaining = item.tradableAfter ? Date.parse(item.tradableAfter) - now : NaN;
  if (!Number.isFinite(remaining)) return '待同步';
  if (remaining <= 0) return '待核验';
  if (remaining >= 86400000) { const hours = Math.floor(remaining / 3600000); return `${Math.floor(hours / 24)}天${hours % 24}小时`; }
  const minutes = Math.min(1439, Math.ceil(remaining / 60000));
  return minutes >= 60 ? `${Math.floor(minutes / 60)}小时${minutes % 60}分钟` : `${minutes}分钟`;
}

export function ledgerPresentation(entry: Pick<WalletLedgerEntry, 'availableDeltaFen' | 'frozenDeltaFen'>) {
  const net = entry.availableDeltaFen + entry.frozenDeltaFen;
  if (net !== 0) return { amount: `${net > 0 ? '+' : ''}${formatPrice(net)}`, tone: net > 0 ? 'credit' : 'debit' };
  if (entry.frozenDeltaFen !== 0) return { amount: `${entry.frozenDeltaFen > 0 ? '冻结' : '解冻'} ${formatPrice(Math.abs(entry.frozenDeltaFen))}`, tone: '' };
  return { amount: formatPrice(0), tone: '' };
}
